import { useId, useState } from 'react'
import { toast } from 'sonner'
import {
  EntryCode,
  JudgingClassMoveConflict,
  type JudgingClassMoveConflictOutput,
  type JudgingClassMoveOutput,
} from '@schemas'
import { useMoveEntryClassDuringJudging } from '@/api/hooks'
import { ClassTypeahead, type ClassOption } from '@/components/ClassTypeahead'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { parseApiErrorMessage, parseApiErrorPayload } from '@/lib/api-errors'

const classLabel = (cls: { code: string; name: string }) => `${cls.code} — ${cls.name}`

function moveSummary(result: JudgingClassMoveOutput): string {
  const target = classLabel(result.class)
  const { entry } = result
  const others = result.disqualifiedEntries.filter(item => item.id !== entry.id)
  return [
    entry.status === 'disqualified'
      ? `Exhibit ID ${entry.entryCode} moved to ${target} and is disqualified.`
      : `Exhibit ID ${entry.entryCode} moved to ${target}.`,
    others.length > 0
      ? `Exhibit ID ${others.map(item => item.entryCode).join(', ')} is disqualified.`
      : null,
    result.reopenedResults ? `Results for ${target} were reopened.` : null,
  ]
    .filter(Boolean)
    .join(' ')
}

type MoveExhibitDialogProps = {
  showId: string
  classOptions: ClassOption[]
}

/**
 * Moves an exhibit the judge found in the wrong class. When the entrant already has an exhibit in
 * the new class, the secretary tells the judge its Exhibit ID and records the one the judge keeps;
 * the server disqualifies the other.
 */
export function MoveExhibitDialog({ showId, classOptions }: MoveExhibitDialogProps) {
  const [open, setOpen] = useState(false)
  const [entryCode, setEntryCode] = useState('')
  const [classId, setClassId] = useState('')
  const [conflict, setConflict] = useState<JudgingClassMoveConflictOutput | null>(null)
  const [keepEntryId, setKeepEntryId] = useState('')
  const [message, setMessage] = useState<string | null>(null)
  const move = useMoveEntryClassDuringJudging()
  const codeFieldId = useId()
  const classFieldId = useId()
  const keepName = useId()

  const reset = () => {
    setEntryCode('')
    setClassId('')
    setConflict(null)
    setKeepEntryId('')
    setMessage(null)
  }
  const changeOpen = (next: boolean) => {
    if (move.isPending) return
    setOpen(next)
    if (!next) reset()
  }

  const submit = async () => {
    setMessage(null)
    const code = entryCode.trim().toUpperCase()
    if (!EntryCode.safeParse(code).success) {
      setMessage('Enter the four-character Exhibit ID.')
      return
    }
    if (!classId) {
      setMessage('Choose the class the exhibit moves to.')
      return
    }
    try {
      const result = await move.mutateAsync({
        showId,
        entryCode: code,
        classId,
        ...(conflict
          ? {
              keepEntryId,
              conflictingEntryIds: conflict.conflictingEntries.map(item => item.id),
            }
          : {}),
      })
      toast.success(moveSummary(result), {
        description:
          result.entry.status === 'disqualified'
            ? 'Set the jar aside; it is not judged.'
            : 'Relabel the jar for its new class.',
      })
      setOpen(false)
      reset()
    } catch (error) {
      const asked = JudgingClassMoveConflict.safeParse(parseApiErrorPayload(error))
      if (asked.success && asked.data.conflictingEntries.length > 0) {
        if (conflict)
          setMessage("The entrant's exhibits in this class changed. Ask the judge again.")
        setConflict(asked.data)
        setKeepEntryId('')
        return
      }
      if (asked.success) setConflict(null)
      setMessage(parseApiErrorMessage(error, 'Unable to move the exhibit. Try again.'))
    }
  }

  const keepOptions = conflict
    ? [
        ...conflict.conflictingEntries.map(item => ({
          id: item.id,
          entryCode: item.entryCode,
          note: `already in ${conflict.class.code}`,
        })),
        {
          id: conflict.entry.id,
          entryCode: conflict.entry.entryCode,
          note: `moving from ${conflict.entry.class.code}`,
        },
      ]
    : []
  const heldSeveral = (conflict?.conflictingEntries.length ?? 0) > 1
  const heldIds = `Exhibit ID${heldSeveral ? 's' : ''} ${conflict?.conflictingEntries
    .map(item => item.entryCode)
    .join(', ')}`

  return (
    <Dialog open={open} onOpenChange={changeOpen}>
      <DialogTrigger asChild>
        <Button type="button" variant="ghost">
          Move exhibit to another class
        </Button>
      </DialogTrigger>
      <DialogContent>
        <form
          className="space-y-4"
          onSubmit={event => {
            event.preventDefault()
            void submit()
          }}
        >
          {conflict ? (
            <>
              <DialogHeader>
                <DialogTitle>
                  Entrant already has {heldIds} in {classLabel(conflict.class)}
                </DialogTitle>
                <DialogDescription>
                  {heldSeveral
                    ? `Tell the judge the entrant already has entries in the class, ${heldIds}, and ask which exhibit stays. The others are disqualified.`
                    : `Tell the judge the entrant already has an entry in the class, ${heldIds}, and ask which exhibit stays. The other is disqualified.`}
                </DialogDescription>
              </DialogHeader>
              <fieldset className="space-y-2">
                <legend className="mb-2 text-sm font-medium">The judge keeps</legend>
                {keepOptions.map(option => (
                  <label
                    key={option.id}
                    className="flex cursor-pointer items-center gap-3 rounded-md border border-border/70 p-3 text-sm"
                  >
                    <input
                      type="radio"
                      name={keepName}
                      value={option.id}
                      checked={keepEntryId === option.id}
                      onChange={() => setKeepEntryId(option.id)}
                      disabled={move.isPending}
                    />
                    <span>
                      Exhibit ID <span className="font-mono font-semibold">{option.entryCode}</span>{' '}
                      <span className="text-muted-foreground">({option.note})</span>
                    </span>
                  </label>
                ))}
              </fieldset>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Move exhibit to another class</DialogTitle>
                <DialogDescription>
                  For an exhibit the judge finds in the wrong class, such as extracted honey of
                  another color. The Exhibit ID stays the same.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2">
                <label htmlFor={codeFieldId} className="text-sm font-medium">
                  Exhibit ID
                </label>
                <Input
                  id={codeFieldId}
                  value={entryCode}
                  onChange={event => setEntryCode(event.target.value)}
                  maxLength={4}
                  autoComplete="off"
                  className="font-mono uppercase"
                  disabled={move.isPending}
                />
              </div>
              <div className="space-y-2">
                <label htmlFor={classFieldId} className="text-sm font-medium">
                  New class
                </label>
                <ClassTypeahead
                  id={classFieldId}
                  value={classId}
                  onChange={setClassId}
                  options={classOptions}
                  disabled={move.isPending}
                  placeholder="Search classes by code or name…"
                />
              </div>
            </>
          )}
          {message ? (
            <p role="alert" className="text-sm text-destructive">
              {message}
            </p>
          ) : null}
          <DialogFooter>
            {conflict ? (
              <Button
                type="button"
                variant="outline"
                disabled={move.isPending}
                onClick={() => {
                  setConflict(null)
                  setKeepEntryId('')
                  setMessage(null)
                }}
              >
                Back
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                disabled={move.isPending}
                onClick={() => changeOpen(false)}
              >
                Cancel
              </Button>
            )}
            <Button type="submit" disabled={move.isPending || (conflict !== null && !keepEntryId)}>
              {move.isPending ? 'Moving…' : conflict ? 'Keep selected exhibit' : 'Move exhibit'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
